import { Badge, Combobox, Group, Loader, Text, TextInput, useCombobox } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { suggestBiomarkers, type BiomarkerSuggestion } from "@/api/authoring";
import { t } from "@/i18n";

/**
 * Biomarker picker with autocomplete (chip 1).
 *
 * Each row shows the **canonical symbol** as a badge and the **LOINC code**
 * as a dimmed secondary label — both, per the 2026-07-09 decision. The two
 * must not be confusable: the symbol is the identity (ADR-0009) and is what
 * selection inserts; LOINC is shown so a clinical author can cross-check
 * against a lab's code sheet, and renders as an em-dash for the ~25% of the
 * catalog (groupers, derived BMI/BP) that has none.
 *
 * When a row matched on a synonym or a near-match, the menu says so
 * ("matched: Blood Sugar") — the most useful signal in a catalog where one
 * symbol carries dozens of aliases across 8 languages.
 *
 * Deliberately **additive**: free-typed text stays submittable (the server
 * resolves synonyms anyway), and an unavailable registry (ADR-0050 degraded
 * mode) silently degrades to a plain text input rather than blocking
 * authoring.
 */

interface BiomarkerComboboxProps {
  value: string;
  onChange: (value: string) => void;
  label?: string | undefined;
  placeholder?: string | undefined;
  /** Uppercase the typed text (symbols are uppercase by convention). */
  uppercase?: boolean;
  /**
   * The field holds a comma-separated list (`GLU, TCH`). Suggestions match the
   * segment after the last comma, and selecting one replaces only that segment
   * — so picking a second biomarker never clobbers the first.
   */
  commaSeparated?: boolean;
  /** Also offer profile attributes (condition, pregnant, …) — the condition
   * builder's attribute picker; the biomarker pickers leave this off. */
  includeProfileAttributes?: boolean;
  /** Fires with the full suggestion when a row is picked (kind, categories). */
  onSelect?: ((suggestion: BiomarkerSuggestion) => void) | undefined;
  "aria-label"?: string | undefined;
}

/** The text a suggestion query should run against: the last comma segment. */
function activeSegment(text: string, commaSeparated: boolean): string {
  if (!commaSeparated) return text;
  return (text.split(",").pop() ?? "").trim();
}

/** Replace the last comma segment with `symbol`, keeping earlier terms. */
function replaceActiveSegment(text: string, symbol: string, commaSeparated: boolean): string {
  if (!commaSeparated) return symbol;
  const parts = text.split(",");
  parts[parts.length - 1] = ` ${symbol}`;
  return parts.join(",").replace(/^\s+/, "");
}

function matchReason(suggestion: BiomarkerSuggestion): string | null {
  if (suggestion.matched_on === "synonym") {
    return `${t("authoring.matched_synonym")} ${suggestion.matched_text}`;
  }
  if (suggestion.matched_on === "fuzzy") {
    return `${t("authoring.matched_fuzzy")} ${suggestion.matched_text}`;
  }
  return null;
}

export function BiomarkerCombobox({
  value,
  onChange,
  label,
  placeholder,
  uppercase = false,
  commaSeparated = false,
  includeProfileAttributes = false,
  onSelect,
  "aria-label": ariaLabel,
}: BiomarkerComboboxProps): JSX.Element {
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });
  const [search, setSearch] = useState(value);
  const query = activeSegment(search, commaSeparated);

  // The query key is the active segment; Tanstack's cache dedupes keystrokes
  // that revisit a prefix, which is most of them.
  const { data, isFetching } = useQuery({
    queryKey: ["biomarker-suggest", query, includeProfileAttributes],
    queryFn: () => suggestBiomarkers(query, 10, includeProfileAttributes),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  const suggestions = data?.suggestions ?? [];

  const options = suggestions.map((suggestion) => {
    const reason = matchReason(suggestion);
    return (
      <Combobox.Option value={suggestion.symbol} key={suggestion.symbol}>
        <Group justify="space-between" wrap="nowrap">
          <div style={{ minWidth: 0 }}>
            <Text size="sm" truncate>
              {suggestion.display}
            </Text>
            {reason !== null && (
              <Text size="xs" c="dimmed" truncate>
                {reason}
              </Text>
            )}
          </div>
          <Group gap="xs" wrap="nowrap">
            <Badge
              variant="light"
              size="sm"
              color={suggestion.kind === "profile" ? "grape" : "blue"}
            >
              {suggestion.symbol}
            </Badge>
            <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {suggestion.kind === "profile"
                ? t("authoring.kind_profile")
                : suggestion.loinc !== null
                  ? `LOINC ${suggestion.loinc}`
                  : "—"}
            </Text>
          </Group>
        </Group>
      </Combobox.Option>
    );
  });

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(symbol) => {
        // Insert the canonical symbol — never the LOINC. In a comma-separated
        // field only the segment being typed is replaced.
        const next = replaceActiveSegment(search, symbol, commaSeparated);
        onChange(next);
        setSearch(next);
        const picked = suggestions.find((s) => s.symbol === symbol);
        if (picked !== undefined) onSelect?.(picked);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={search}
          rightSection={isFetching ? <Loader size={16} /> : null}
          onChange={(event) => {
            const next = uppercase
              ? event.currentTarget.value.toUpperCase()
              : event.currentTarget.value;
            setSearch(next);
            onChange(next); // free text stays submittable
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={query.length < 2}>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>
              {isFetching ? t("authoring.suggest_loading") : t("authoring.suggest_empty")}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
